import type { ReactNode } from 'react'
import { Button } from '@/components/Button'
import { Checkbox } from '@/components/Checkbox'
import {
  SubmissionSection,
  SubmissionSectionHeader,
} from '@/features/jobs/drafting/components/JobCreation/Submission/SubmissionSection'
import styles from './Billing.module.css'

export interface BillingProps {
  compact?: boolean
  /**
   * The rendered selected payment method; when absent, the add-new prompt is shown.
   */
  selectedPaymentMethod?: ReactNode
  canChange: boolean
  isDisabled: boolean
  onChangePaymentMethod: () => unknown
  onAddPaymentMethod: () => unknown
  testId?: string
}

export function Billing({
  compact,
  selectedPaymentMethod,
  canChange,
  isDisabled,
  onChangePaymentMethod,
  onAddPaymentMethod,
  testId,
}: BillingProps) {
  return (
    <SubmissionSection compact={compact}>
      <SubmissionSectionHeader
        label="Select a Payment Method"
        compact={compact}
      >
        {selectedPaymentMethod && canChange && (
          <Button
            label="Change"
            size={compact ? 'normal' : 'baseline'}
            variant="text"
            isDisabled={isDisabled}
            onPress={onChangePaymentMethod}
          />
        )}
      </SubmissionSectionHeader>
      {selectedPaymentMethod ? (
        <SelectedPaymentMethod>{selectedPaymentMethod}</SelectedPaymentMethod>
      ) : (
        <AddNewPaymentMethod
          isDisabled={isDisabled}
          canChange={canChange}
          onPress={onAddPaymentMethod}
          testId={testId}
        />
      )}
    </SubmissionSection>
  )
}

export interface SelectedPaymentMethodProps {
  children?: ReactNode
}

export function SelectedPaymentMethod({
  children,
}: SelectedPaymentMethodProps) {
  return <div>{children}</div>
}

export interface AddNewPaymentMethodProps {
  isDisabled: boolean
  canChange: boolean
  onPress: () => unknown
  testId?: string
}

export function AddNewPaymentMethod({
  isDisabled,
  canChange,
  onPress,
  testId,
}: AddNewPaymentMethodProps) {
  return (
    <div className={styles.add}>
      <Button
        label="Add Payment Method"
        block
        variant="text"
        isDisabled={isDisabled || !canChange}
        onPress={onPress}
        testId={testId && `${testId}-add-payment-method-button`}
        title={
          canChange
            ? 'Add a payment method'
            : 'You are not allowed to manage billing.'
        }
      />
    </div>
  )
}

export interface AdminBillingProps {
  bypassBilling: boolean
  onBypassBillingChange: (value: boolean) => unknown
}

/**
 * Admin Billing
 *
 * Determines whether the current job's owner payment method is used or not.
 */
export function AdminBilling({
  bypassBilling,
  onBypassBillingChange,
}: AdminBillingProps) {
  return (
    <SubmissionSection>
      <label className={styles.billExternal}>
        Bypass billing{' '}
        <Checkbox checked={bypassBilling} onChange={onBypassBillingChange} />
      </label>
      <p className={styles.billExternalHelp}>
        Should we charge the default billing method or will this be handled
        manually.
      </p>
    </SubmissionSection>
  )
}
